import { action, makeAutoObservable, observable } from "mobx";
import { ChangeEvent, FormEvent } from "react";
import { createCommunityApiCall } from "../../api/onboard/createCommunity";
import { sendEditCommunityApiRequest } from "../../api/onboard/editCommunityApi";
import { getDomainListApiCall } from "../../api/onboard/getDomainListApi";
import { organizationDetailsApiCall } from "../../api/onboard/getOrganizationDetailsApiCall";
import { domainSuggestionApiCall } from "../../api/onboard/subDomainSuggestionApiCall";
import { CreateCommunityModel } from "../../datamodels/onboarding/createCommunityModel";
import { DomainListModel } from "../../datamodels/onboarding/domainListModel";
import { DomainSuggestionModel } from "../../datamodels/onboarding/domainSuggestionModel";
import { DefaultModel } from "../../datamodels/onboarding/defaultModel";
import { OrganizationDetailsModel } from "../../datamodels/onboarding/organizationDetailsModel";
import { DefaultChannelListModel } from "../../datamodels/services/defaultChannelsModel";
import { sendHomeDefaultChannelListApiCall } from "../../api/services/defaultChannelsApi";
import { sendUploadUsersEmailApi } from "../../api/onboard/importUsersEmailApi";
import { sendUploadUsersPhoneApi } from "../../api/onboard/importUsersPhone";
import { checkWebsiteHealthApiCall } from "../../api/services/checkHealthApi";
import { ProfileTokenModel } from "../../datamodels/auth/profileTokenModel";
import { getProfileTokenApiCall } from "../../api/auth/getProfileTokenApi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../css/toastify.css";
import { UserHivesModel } from "../../datamodels/services/userHivesModel";
import { getUserHivesApiCall } from "../../api/services/getUserHives";
import { ctaClickedApiCall } from "../../api/services/ctaAnalytics";
import { getOtherHivesApiCall } from "../../api/services/getOtherHives";
import { OtherHivesModel } from "../../datamodels/services/otherHivesModel";

class MainViewModel {
  private dom: Document;

  constructor(document: Document) {
    makeAutoObservable(this);
    this.dom = document;
  }

  @observable public localDescription = localStorage.getItem("description");

  @observable public currentPage = "home";
  @observable public goToPage = "/";

  @observable public recaptchaToken: string | null = "";

  @action printPage = () => {
    console.log(this.currentPage);
  };

  @observable public communityId: number | undefined;

  @observable public toDescription: boolean = false;

  @action goToSignup = () => {
    this.dom.location.href = "signup";
  };

  //Create Community
  @observable public communityName: string = "";

  @observable public nextIsValid: boolean = false;
  @observable public needSuggestions: boolean = false;
  @observable public communityNameLength: number = 18;
  @observable public tempSubDomainName: string = "";

  @action setCommunityName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const shouldSetValue = e.target.value.length < 19;

    if (shouldSetValue) {
      this.communityName = e.target.value;
      this.tempSubDomainName = this.communityName
        .toLowerCase()
        .replace(" ", "")
        .slice(0, 9);
      this.tempSubDomainName = this.tempSubDomainName.replace(/[^a-z0-9]/g, "");
      if (!this.domainListResp?.data.includes(this.tempSubDomainName)) {
        this.communitySubDomain = this.tempSubDomainName;
        this.validDomain = true;
      }
    } else {
      this.communityName = e.target.value.slice(0, 18);
    }
    this.communityNameLength = 18 - this.communityName.length;
    if (this.communityName.length < 2 || this.communitySubDomain.length < 2) {
      this.nextIsValid = false;
    } else {
      this.nextIsValid = true;
    }
  };

  @observable public communitySubDomain: string = "";

  @action setDomain = (e: string) => {
    this.communitySubDomain = e;

    if (
      this.communityName.length < 2 ||
      this.communitySubDomain.length < 2 ||
      this.communitySubDomain.length > 20
    ) {
      this.nextIsValid = false;
    } else {
      if (this.domainListResp?.data.includes(this.communitySubDomain)) {
        this.getDomainSuggestions();
        setTimeout(() => {
          this.needSuggestions = true;
          this.nextIsValid = false;
        }, 500);
      } else {
        setTimeout(() => {
          this.needSuggestions = false;
          this.nextIsValid = true;
        }, 500);
      }
    }
  };

  @observable public domainErrorMessage: string = "";
  @observable public validDomain: boolean = false;

  @action setCommunitySubDomain = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.communitySubDomain = e.target.value.slice(0, 9);
    if (/^[a-z0-9-]+$/g.test(this.communitySubDomain)) {
      this.validDomain = true;
      this.domainErrorMessage = "";
    } else if (this.communitySubDomain === "") {
      this.domainErrorMessage = "";
    } else {
      this.validDomain = false;
      this.domainErrorMessage =
        "Sorry, only lowercase letters (a-z), numbers (0-9) and hyphens (-) are allowed";
    }
    if (this.communitySubDomain[0] === "-") {
      this.domainErrorMessage =
        "Sorry, domain name cannot start with a hyphen (-)";

      this.validDomain = false;
    }

    if (this.communityName.length < 2 || this.communitySubDomain.length < 2) {
      this.nextIsValid = false;
    } else {
      if (this.domainListResp?.data.includes(this.communitySubDomain)) {
        this.getDomainSuggestions();
        this.nextIsValid = false;
        setTimeout(() => {
          this.needSuggestions = true;
        }, 500);
      } else {
        this.nextIsValid = true;
        setTimeout(() => {
          this.needSuggestions = false;
        }, 500);
      }
    }
  };

  @observable domainListResp: DomainListModel | undefined;

  @action getDomainList = async (): Promise<void> => {
    this.domainListResp = await getDomainListApiCall();
  };

  @observable public createCommunityResp: CreateCommunityModel | undefined;
  @observable public createCommunityClicked: boolean = false;

  @action createCommunity = async (isPrivate: boolean): Promise<void> => {
    this.createCommunityClicked = true;
    this.recordCtaClicked(
      {
        organizationName: this.communityName,
        communitySubDomain: this.communitySubDomain,
        marketPlace: isPrivate ? "PRIVATE" : "COMMUNITY",
      },
      "create_hive",
      "createHive",
      "Create",
      true
    );
    this.createCommunityResp = await createCommunityApiCall(
      this.communityName,
      this.communitySubDomain,
      isPrivate,
      this.recaptchaToken
    );
    if (this.createCommunityResp.responseInfo.httpCode !== 200) {
      if (this.createCommunityResp.data === null) {
        this.getDomainSuggestions();
      }
      this.createCommunityClicked = false;
      this.showToastError(this.createCommunityResp.responseInfo.description);
    } else if (this.createCommunityResp.data.communitySubDomain) {
      this.communityId = this.createCommunityResp.data.id;
      localStorage.setItem("communityId", this.communityId.toString());
      setTimeout(() => {
        this.toDescription = true;
        this.createCommunityClicked = false;
      }, 7000);
    }
    this.recaptchaToken = "";
  };

  // Get organization details
  @observable organizationDetails: OrganizationDetailsModel | undefined;
  @observable gotOrgDetails: boolean = false;

  @action getOrgDetails = async (): Promise<void> => {
    this.gotOrgDetails = false;
    this.organizationDetails = await organizationDetailsApiCall(
      localStorage.getItem("communityId")!
    );
    if (this.organizationDetails.responseInfo.httpCode === 200) {
      this.communityId = this.organizationDetails.data.objOrganizationInfo.id;
    }
    this.gotOrgDetails = true;
  };

  // Get Sub Domain Suggestions
  @observable public domainSuggestions: DomainSuggestionModel | undefined;

  @action getDomainSuggestions = async (): Promise<void> => {
    this.domainSuggestions = await domainSuggestionApiCall(
      this.communitySubDomain
    );
  };

  //To invite
  @observable public toInvite: boolean = false;

  //Who are we
  @action setDummyDescription = () => {
    this.dummyDescription =
      "Digital hives often have features like forums, chat rooms, blogs, and other tools for communication and collaboration. They can be used for a variety of purposes, such as networking, knowledge-sharing, and socializing.";
  };

  @observable dummyDescription: string =
    "Digital hives often have features like forums, chat rooms, blogs, and other tools for communication and collaboration. They can be used for a variety of purposes, such as networking, knowledge-sharing, and socializing.";
  @observable description: string = "";
  @observable public clickedDescription: boolean = false;
  @observable public invalidDesc: string = "";

  @action setDescription = (text: string) => {
    this.description = text;
    this.setCount(text.length);
  };

  @action launchLogin = () => {
    const redirect = process.env.REACT_APP_LOGIN_URL;

    window.open(redirect, "_self");
  };

  //Update long Description
  @observable updateOrgResp: DefaultModel | undefined;

  @action updateCommunity = async (): Promise<void> => {
    this.recordCtaClicked(
      {
        communityName:
          this.organizationDetails?.data.objOrganizationInfo.organizationName!,

        communityTier:
          this.organizationDetails?.data.objOrganizationInfo.organizationTier!,

        communityLogo: "",
        communityBanner: "",
        marketPlace:
          this.organizationDetails?.data.objOrganizationInfo.marketPlace!,

        description: "",
        longDescription: this.description,

        communityUuid:
          this.organizationDetails?.data.objOrganizationInfo.organizationUUID!,

        communitySubDomain: "",
        communityId:
          this.organizationDetails?.data.objOrganizationInfo.id.toString()!,

        communityBio: "",
        communityNameFlag: false,
        communityTierFlag: false,
        marketPlaceFlag: false,
        descriptionFlag: false,
        communitySubDomainFlag: false,
        communityBioFlag: false,
        communityLogoFlag: false,
        communityBannerFlag: false,
        longDescriptionFlag: true,
        introVideo: "",
        introVideoFlag: false,
      },
      "edit_organization",
      "who_are_we",
      "Create",
      true
    );
    this.clickedDescription = true;
    this.updateOrgResp = await sendEditCommunityApiRequest(
      this.organizationDetails?.data.objOrganizationInfo.organizationTier!,
      this.organizationDetails?.data.objOrganizationInfo.marketPlace!,
      this.organizationDetails?.data.objOrganizationInfo.organizationName!,
      this.organizationDetails?.data.objOrganizationInfo.id.toString()!,
      this.organizationDetails?.data.objOrganizationInfo.organizationUUID!,
      this.description
    );
    if (this.updateOrgResp.responseInfo.httpCode === 200) {
      this.goToPage = "invite";
    }
    this.clickedDescription = false;
    this.getOrgDetails();
  };

  @observable public count: number = 280;

  @action setCount = (valueLength: number) => {
    this.count = 280 - valueLength;
  };

  // Invite Users
  @observable public importCount: number = 0;

  @observable public csvErrorMessage: string = "";
  @observable public csvSuccessMessage: string = "";
  @observable public importUserEmailList: string[] = [];
  @observable public importUserEmailListObjects: object[] = [];

  @action splitImportUserEmailField = (): void => {
    // this.importUserEmailList = this.importUserEmailField.split(";");
    this.importUserEmailListObjects = this.emails!.map((item) => {
      return { email: item };
    });
  };

  @action splitCSVImportEmail = (): void => {
    this.importUserEmailListObjects = this.importUserEmailList.map((item) => {
      return { email: item };
    });
  };

  // @action splitCSVImportPhone = (): void => {
  //   this.importUserEmailListObjects = this.importUserEmailList.map((item) => {
  //     return { email: item };
  //   });
  // };
  @observable public importUserEmailField: string = "";

  @action handleImportUserEmailFieldChange = (event: ChangeEvent): void => {
    const element = event.currentTarget as HTMLInputElement;
    this.importUserEmailField = element.value;
    this.splitImportUserEmailField();
  };

  @observable public importUserPhoneField: string = "";

  //Email Invite

  @observable public emailInviteResp: DefaultModel | undefined;

  @action inviteEmailUsers = async (): Promise<void> => {
    this.recordCtaClicked(
      {
        users: this.importUserEmailListObjects,
        channels: this.channelList,
        communityId: +localStorage.getItem("communityId")!,
      },
      "invite_email",
      "invite",
      "Create",
      true
    );
    this.emailInviteResp = await sendUploadUsersEmailApi(
      this.importUserEmailListObjects,
      this.channelList,
      +localStorage.getItem("communityId")!
    );
  };

  //Phone Invite
  @action splitImportUserPhoneField = (): void => {
    // this.importUserEmailList = this.importUserEmailField.split(";");
    this.phoneNumberListObjects = this.phoneNos!.map((item) => {
      return { mobileNo: item };
    });
  };

  @observable public phoneInviteResp: DefaultModel | undefined;

  @action invitePhoneUsers = async (): Promise<void> => {
    this.recordCtaClicked(
      {
        users: this.phoneNumberListObjects,
        channels: this.channelList,
        communityId: +localStorage.getItem("communityId")!,
      },
      "invite_phone_number",
      "invite",
      "Create",
      true
    );
    this.emailInviteResp = await sendUploadUsersPhoneApi(
      this.phoneNumberListObjects,
      this.channelList,
      +localStorage.getItem("communityId")!
    );
  };

  //Input Field
  @observable public phoneInvite: boolean = false;

  @action handleEmailButton = () => {
    this.phoneInvite = false;
  };

  @action handlePhoneButton = () => {
    this.phoneInvite = true;
  };

  //Emails
  @observable emails: string[] = [];

  //Phone numbers
  @observable phoneNumbers: { id: string; text: string }[] = [];
  @observable phoneNos: string[] = [];
  @observable phoneNumberListObjects: object[] = [];

  @observable phoneNumbersAddition = (numbers: string[]) => {
    numbers.map((data) => {
      return this.phoneNos.push(data);
    });
  };

  //Invite Users
  @action inviteUsers = () => {
    if (this.phoneInvite) {
      this.splitImportUserPhoneField();
      this.invitePhoneUsers();
    } else {
      this.splitImportUserEmailField();
      this.inviteEmailUsers();
    }
  };

  //Get Channel List
  @observable public channelList: string[] = [];

  @observable public defaultChannelList: DefaultChannelListModel | undefined;
  @action getdefaultChannels = async (): Promise<void> => {
    this.defaultChannelList = await sendHomeDefaultChannelListApiCall(
      localStorage.getItem("communityId")!
    );
    if (this.defaultChannelList.responseInfo.httpCode === 200) {
      this.addChannelList();
    }
  };

  @action addChannelList = () => {
    this.defaultChannelList!.data.map((data) => {
      if (!this.channelList.includes(data.channelUUID)) {
        this.channelList.push(data.channelUUID);
      }
    });
  };

  //Finish
  @observable public goToFinish: boolean = false;
  @observable public skipClicked: boolean = false;

  @action setFinish = (clicked: boolean) => {
    this.skipClicked = clicked;
    this.goToFinish = true;
  };

  // Check Health

  @observable public healthResp: DefaultModel | undefined;
  @observable public checkHealthRunning: boolean = false;
  @observable public redirectEnabled: boolean = false;

  @action checkHealth = async (): Promise<void> => {
    if (this.checkHealthRunning === false) {
      this.checkHealthRunning = true;
      console.log("called");
      this.healthResp = await checkWebsiteHealthApiCall(
        +localStorage.getItem("communityId")!
      );
      if (this.healthResp.responseInfo.httpCode === 200) {
        this.redirectEnabled = true;
      } else {
        this.redirectEnabled = false;
        if (this.loadingIndex < 5) {
          this.updateLoadingHistory();
        }
        setTimeout(() => {
          this.checkHealth();
        }, 10000);
      }
      this.checkHealthRunning = false;
    }
  };

  //Get user Hives
  @observable public userHives: UserHivesModel | undefined;
  @observable public gotUserHives: boolean = false;

  @action getUserHives = async (): Promise<void> => {
    this.gotUserHives = false;
    this.userHives = await getUserHivesApiCall();
    if (this.userHives.responseInfo.httpCode === 200) {
    }
    this.gotUserHives = true;
  };
  //Get user Hives
  @observable public otherHives: OtherHivesModel | undefined;
  @observable public gotOtherHives: boolean = false;

  @action getOtherHives = async (): Promise<void> => {
    this.gotOtherHives = false;
    this.otherHives = await getOtherHivesApiCall();
    if (this.otherHives.responseInfo.httpCode === 200) {
    }
    this.gotOtherHives = true;
  };

  // Loading

  @observable public loadingHistory: string[] = [];
  @observable public currLoadingStatus: string = "Hosting your domain name ...";
  @observable public loadingStages = [
    "",
    "Hosting your domain name ...",
    "Uploading data to the cloud ...",
    "Setting up your home screen ...",
    "Creating channel ...",
    "Optimizing your Dashboard ...",
  ];
  @observable loadingIndex = 0;

  @action updateLoadingHistory = () => {
    this.loadingHistory.push(this.loadingStages[this.loadingIndex]);
    this.currLoadingStatus = this.loadingStages[this.loadingIndex + 1];

    this.loadingIndex += 1;
  };

  //Get Profile Token
  @observable public profileTokenResp: ProfileTokenModel | undefined;

  @action getProfileToken = async (): Promise<void> => {
    this.profileTokenResp = await getProfileTokenApiCall();
    if (this.profileTokenResp.responseInfo.httpCode === 200) {
      // this.launchDashboard(this.profileTokenResp.data);
    }
  };

  @action recordCtaClicked = (
    passedReq: object | null,
    passedCtaName: string,
    passedPageName: string,
    passedAppName: string,
    passedAuthFlag: boolean
  ) => {
    ctaClickedApiCall(
      passedReq,
      passedCtaName,
      passedPageName,
      passedAppName,
      passedAuthFlag
    );
  };

  // Launch Dashboard
  @observable public setLaunchClicked: boolean = false;

  @action launchDashboard = () => {
    this.setLaunchClicked = true;
    setTimeout(() => {
      window.open(
        `https://${
          this.organizationDetails?.data.objOrganizationInfo.communitySubDomain
        }.vhive.org?login=${localStorage.getItem("@JWTToken")}`,
        "_self"
      );
    }, 3000);
  };

  // Launch Development Environment
  // @action openDevEnv = (passedToken: string) => {
  //   window.open(
  //     // `https://appdev.vhive.org/`,
  //     `http://localhost:3000?token=${passedToken}`,
  //     "_blank"
  //   );
  // };
  // Launch User Hive
  @action launchUserHive = (passedDomain: string) => {
    window.open(
      `https://${passedDomain}.vhive.org/?login=${localStorage.getItem(
        "@JWTToken"
      )}`,
      "_blank"
    );
  };

  //Toasts
  @action showToastError = (message: string) => {
    toast.error(message, {
      className: "font",
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };
  @action showToastSuccess = (message: string) => {
    toast.success(message, {
      className: "font",
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };
}

export default MainViewModel;
